import { Link, useLocation } from "react-router-dom";
import cn from "classnames";
import logo from "../assets/imgs/aci6-header.webp";
import logoSm from "../assets/imgs/aci6-header-192.webp";
import "../styles/header.scss";
import Dropdown from "./Dropdown";
import Sidebar from "./Sidebar";
import { useState, useMemo, useEffect } from "react";
import { CustomHeaderPaths, MENU } from "../configs/header";
import HeaderHome from "./Homepage/Header";
import { Menu } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const TEXT_SRC = `${process.env.PUBLIC_URL || ""}/logo-header-text.webp`;
const TEXT_SRC_SM = `${process.env.PUBLIC_URL || ""}/logo-header-text-800.webp`;

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(false);
  const location = useLocation();

  const isHome = useMemo(
    () => CustomHeaderPaths.includes(location.pathname),
    [location.pathname],
  );

  const toggleSidebar = () => {
    setIsSidebarOpen((prev) => !prev);
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 96);
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const headerVariants = {
    initial: {
      opacity: 0,
      y: 8,
      x: "-50%",
    },
    animate: {
      opacity: 1,
      y: 0,
      x: "-50%",
    },
    exit: {
      opacity: 0,
      y: -10,
      x: "-50%",
    },
  };

  const HeaderContent = () => {
    return <div className="flex justify-between items-start lg:justify-center pt-4 lg:pt-0 px-2 lg:px-0">
      <div className="flex items-center lg:-ml-24">
        <Link
          to="/"
          className="aspect-square w-screen-1/5 lg:w-40 h-screen-w-1/5 lg:h-48 min-vw-100 min-w-[80px] min-h-[80px] lg:p-8"
        >
          <img
            src={logo}
            srcSet={`${logoSm} 192w, ${logo} 512w`}
            sizes="(min-width: 1024px) 10rem, 20vw"
            className="h-full w-full object-contain"
            alt="ASA 2 logo"
            width={512}
            height={512}
            loading="eager"
            decoding="async"
          />
        </Link>
        <img
          src={TEXT_SRC}
          srcSet={`${TEXT_SRC_SM} 800w, ${TEXT_SRC} 1200w`}
          sizes="(min-width: 1024px) 30rem, 80vw"
          className="w-screen min-w-[160px] lg:w-120 mx-auto"
          alt="Acireale 6"
          width={1200}
          height={300}
          loading="eager"
          decoding="async"
          fetchPriority="high"
        />
      </div>
      <button
        type="button"
        onClick={toggleSidebar}
        className="lg:hidden pr-6 pt-8"
        aria-haspopup="dialog"
        aria-expanded={isSidebarOpen}
        aria-controls="sidebar"
        aria-label="Apri menu"
      >
        <Menu color="white" aria-hidden="true" focusable="false" />
        <span className="sr-only">Apri menu</span>
      </button>
    </div>
  }

  return (
    <>
      <div
        className="header-wrapper"
        style={{ position: "relative", zIndex: 100 }}
      >
        <AnimatePresence mode="wait">
          {isHome ? (
            <motion.div
              key="home-header"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
            >
              <HeaderHome
                toggleSidebar={toggleSidebar}
                isSidebarOpen={isSidebarOpen}
              />
            </motion.div>
          ) : (
            <motion.header
              key="standard-header"
              variants={headerVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={{ duration: 0.3, ease: "easeOut" }}
              // Rimosso -translate-x-1/2 dalle classi perché ora è gestito da Framer Motion (x: "-50%")
              className="absolute z-50 top-0 left-1/2 w-full overflow-visible"
            >
              <HeaderContent />
              <div className="hidden lg:block">
                <ul className="menu flex justify-center gap-8 text-white">
                  {MENU.filter((m) => m.header).map((item) => (
                    <li key={item.text.replace(" ", "")}>
                      {!item.items.length || item.hideItemsOnHeader ? (
                        <Link to={item.link}>{item.text}</Link>
                      ) : (
                        <Dropdown
                          text={item.text}
                          link={item.link}
                          items={item.items}
                        />
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            </motion.header>
          )}
        </AnimatePresence>
        <div className={cn(
            'fixed z-[60] -top-28 md:-top-56 w-full transition-all',
            'lg:hidden',
            {
              '!top-0': isScrolled
            }
          )}>
          <div className="bg-header bg-blur m-1">
            <div className="scale-90">
              <HeaderContent />
            </div>
          </div>
        </div>
      </div>

      <Sidebar
        isSidebarOpen={isSidebarOpen}
        toggleSidebar={toggleSidebar}
        menu={MENU.filter((m) => m.header)}
      />
    </>
  );
};

export default Header;
